import { Check, Gift, Minus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../../components/ui/button";
import { cn } from "../../../handlers/cn.utils";
import { currencyFormat } from "../../../handlers/utilityFunctions";
import useIsLoggedIn from "../../../hooks/use-IsLoggedIn";
import { ResumeDataService } from "../../../services/create-resume/resume-data";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SubscriptionOptionsProps {
	setSelectedPackage: (pkg: string) => void;
	usePoints: boolean;
	setUsePoints: (val: boolean) => void;
}

interface PlanData {
	id: string;
	name: string;
	tagline: string;
	price: string;
	priceNum: number;
	pointsPrice: string | null;
	pointsPriceNum: number | null;
	strikePrice: string | null;
	discountLabel: string | null;
	isFree: boolean;
	isRecommended: boolean;
	features: string[];
	inheritsFrom: string | null;
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const plans: PlanData[] = [
	{
		id: "bronze",
		name: "Bronze",
		tagline: "The quickest way to try Dproz",
		price: "0",
		priceNum: 0,
		pointsPrice: null,
		pointsPriceNum: null,
		strikePrice: null,
		discountLabel: null,
		isFree: true,
		isRecommended: false,
		features: [
			"Build 1 resume with step-by-step guidance",
			"Access to standard resume templates",
			"Systematic completeness check",
			"Expertly crafted contents",
		],
		inheritsFrom: null,
	},
	{
		id: "silver",
		name: "Silver",
		tagline: "14 days full access",
		price: "1,950",
		priceNum: 1950,
		pointsPrice: "750",
		pointsPriceNum: 750,
		strikePrice: null,
		discountLabel: null,
		isFree: false,
		isRecommended: false,
		features: ["Build 1 additional resume tailored to a specific job (2 total)", "Unlimited resume downloads & share"],
		inheritsFrom: "Bronze",
	},
	{
		id: "gold",
		name: "Gold",
		tagline: "60 days full access",
		price: "5,850",
		priceNum: 5850,
		pointsPrice: "2,250",
		pointsPriceNum: 2250,
		strikePrice: "8,500",
		discountLabel: "Save 69%",
		isFree: false,
		isRecommended: true,
		features: [
			"Build 2 additional resumes for specific jobs (4 total)",
			"Request & receive unlimited resume improvement suggestions",
			"Unlimited access to premium templates",
		],
		inheritsFrom: "Silver",
	},
	{
		id: "platinum",
		name: "Platinum",
		tagline: "12 months full access",
		price: "51,500",
		priceNum: 51500,
		pointsPrice: "19,828",
		pointsPriceNum: 19828,
		strikePrice: null,
		discountLabel: null,
		isFree: false,
		isRecommended: false,
		features: [
			"Build 3 additional resumes for specific jobs (7 total)",
			"2 rounds of async resume review by 2 Dproz experts",
			"Unlimited access to premium templates",
			"1 round (45 min) career conversations with Dproz Expert",
		],
		inheritsFrom: "Gold",
	},
];

type CellValue = boolean | string;

interface TableSection {
	title: string;
	rows: { label: string; values: [CellValue, CellValue, CellValue, CellValue] }[];
}

const tableSections: TableSection[] = [
	{
		title: "Core Features",
		rows: [
			{ label: "Step-by-step Guidance", values: [true, true, true, true] },
			{ label: "Expertly Crafted Contents", values: [true, true, true, true] },
			{ label: "Completeness Check", values: [true, true, true, true] },
			{ label: "Standard Templates", values: [true, true, true, true] },
			{ label: "Unlimited Edits", values: [true, true, true, true] },
			{ label: "Targeted Resumes", values: ["1", "2", "4", "7"] },
		],
	},
	{
		title: "Premium Features",
		rows: [
			{ label: "Premium Templates", values: [false, false, true, true] },
			{ label: "PDF Downloads & Shares", values: [false, true, true, true] },
			{ label: "Resume Improvement Suggestions", values: [false, false, true, true] },
		],
	},
	{
		title: "Expert Support",
		rows: [
			{ label: "Expert Async Reviews", values: [false, false, false, "2 rounds, 2 experts"] },
			{ label: "Career Conversations", values: [false, false, false, "1 round (45 min)"] },
		],
	},
	{
		title: "Pricing",
		rows: [
			{ label: "Full Access Duration", values: ["\u2014", "14 days", "60 days", "365 days"] },
			{ label: "Cost (TZS)", values: ["FREE", "1,950", "5,850", "51,500"] },
			{
				label: "Cost Per Day (TZS)",
				values: ["0", `${Math.floor(1950 / 14)}`, `${Math.floor(5850 / 60)}`, `${Math.floor(51500 / 365)}`],
			},
		],
	},
];

const PLAN_NAMES = ["Bronze", "Silver", "Gold", "Platinum"] as const;

const dotGridStyle: React.CSSProperties = {
	backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 0)",
	backgroundSize: "20px 20px",
};

/* ------------------------------------------------------------------ */
/*  Cell renderer                                                      */
/* ------------------------------------------------------------------ */

function CellContent({ value }: { value: CellValue }) {
	if (value === true) return <Check className="mx-auto h-4 w-4 text-blue-600" />;
	if (value === false) return <Minus className="mx-auto h-3.5 w-3.5 text-gray-300" />;
	return <span className="tabular-nums">{value}</span>;
}

/* ------------------------------------------------------------------ */
/*  Plan Card                                                          */
/* ------------------------------------------------------------------ */

function PlanCard({
	plan,
	usePoints,
	onSelect,
}: {
	plan: PlanData;
	usePoints: boolean;
	onSelect: (id: string) => void;
}) {
	const isGold = plan.isRecommended;
	const showPointsPrice = usePoints && plan.pointsPrice;

	return (
		<div
			className={cn(
				"relative flex flex-col rounded-xl transition-shadow duration-200",
				isGold
					? "bg-blue-800 shadow-xl shadow-blue-900/25 lg:-mt-6 lg:pb-6"
					: plan.isFree
						? "border border-gray-200 bg-gray-50/70"
						: "border !border-blue-100 bg-blue-50/50 hover:shadow-md",
			)}
		>
			{isGold && <div className="pointer-events-none absolute inset-0 rounded-xl" style={dotGridStyle} />}

			<div className={cn("relative flex flex-1 flex-col", isGold ? "px-6 pb-6 pt-8" : "p-5")}>
				{/* Badge */}
				{isGold && (
					<span className="absolute -top-3.5 left-5 rounded-full bg-white px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-widest text-blue-800 shadow-sm">
						Best Value
					</span>
				)}

				{/* Plan name + tagline */}
				<div className="mb-5">
					<span className={cn("text-[15px] font-bold", isGold ? "text-white" : "text-gray-900")}>{plan.name}</span>
					<p className={cn("mt-0.5 text-[13px]", isGold ? "text-blue-200/60" : "text-gray-500")}>{plan.tagline}</p>
				</div>

				{/* Price — the hero moment */}
				<div className={cn("mb-6", isGold ? "min-h-[80px]" : "min-h-[64px]")}>
					{plan.isFree ? (
						<p className="text-[40px] font-extrabold leading-none tracking-tight text-gray-900">Free</p>
					) : (
						<>
							{plan.strikePrice && !usePoints && (
								<p
									className={cn(
										"mb-1 text-[13px] tabular-nums line-through",
										isGold ? "text-blue-300/40" : "text-gray-400",
									)}
								>
									Tsh {plan.strikePrice}/=
								</p>
							)}
							{showPointsPrice && (
								<p
									className={cn(
										"mb-1 text-[13px] tabular-nums line-through",
										isGold ? "text-blue-300/40" : "text-gray-400",
									)}
								>
									Tsh {plan.price}/=
								</p>
							)}
							<p
								className={cn(
									"font-extrabold leading-none tracking-tight tabular-nums",
									isGold ? "text-[40px] text-white" : "text-[32px] text-gray-900",
								)}
							>
								Tsh {showPointsPrice ? plan.pointsPrice : plan.price}
								<span
									className={cn(
										"font-normal tracking-normal",
										isGold ? "text-base text-blue-300/50" : "text-sm text-gray-400",
									)}
								>
									/=
								</span>
							</p>
							{plan.discountLabel && !usePoints && (
								<span
									className={cn(
										"mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold",
										isGold ? "bg-blue-600/60 text-blue-100" : "bg-blue-100 text-blue-700",
									)}
								>
									{plan.discountLabel}
								</span>
							)}
						</>
					)}
				</div>

				{/* CTA */}
				<div className="mb-6">
					{plan.isFree ? (
						<div className="flex h-11 items-center justify-center rounded-lg bg-gray-200/70 text-sm font-medium text-gray-500">
							Included
						</div>
					) : (
						<Button
							variant={isGold ? "default" : "outlinePrimary"}
							className={cn("h-11 w-full font-semibold", isGold && "bg-white text-blue-800 hover:bg-blue-50")}
							onClick={() => onSelect(plan.id)}
						>
							Select {plan.name}
						</Button>
					)}
				</div>

				{/* Features divider */}
				<div className={cn("border-t pt-5", isGold ? "border-blue-600/30" : "border-gray-200/80")}>
					<p
						className={cn(
							"mb-3 text-[11px] font-bold uppercase tracking-widest",
							isGold ? "text-blue-300/40" : "text-gray-400",
						)}
					>
						{plan.inheritsFrom ? `Everything in ${plan.inheritsFrom}, plus` : "Features"}
					</p>
					<ul className="space-y-3">
						{plan.features.map((feature) => (
							<li
								key={feature}
								className={cn(
									"flex items-start gap-2.5 text-[13px] leading-snug",
									isGold ? "text-blue-100/80" : "text-gray-600",
								)}
							>
								<Check className={cn("mt-[2px] h-4 w-4 shrink-0", isGold ? "text-blue-300" : "text-blue-600")} />
								<span>{feature}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

function SubscriptionOptions({ setSelectedPackage, usePoints, setUsePoints }: SubscriptionOptionsProps) {
	const [availablePoints, setAvailablePoints] = useState<number | null>(null);
	const apiRef = useRef(new ResumeDataService());
	const isLoggedIn = useIsLoggedIn();

	useEffect(() => {
		// Reward points only exist for authenticated users. Skipping the call when
		// logged out keeps the pricing page public — the request would otherwise 401
		// and trip the global axios interceptor that redirects to /signin.
		if (!isLoggedIn) return;

		apiRef.current.getPaymentsRewards(
			(data: { data: Array<{ rewardStatus: string; rewardPoints: number }> }) => {
				const total = data.data
					.filter((r) => r.rewardStatus === "CLAIMED" && r.rewardPoints > 0)
					.reduce((sum, r) => sum + r.rewardPoints, 0);
				setAvailablePoints(total);
			},
			() => setAvailablePoints(0),
		);
	}, [isLoggedIn]);

	return (
		<section className="py-6 sm:py-10">
			<div className="mx-auto max-w-7xl">
				{/* Header */}
				<div className="mb-10 max-w-2xl">
					<h3 className="text-[32px] font-extrabold leading-[1.1] tracking-tight text-gray-900 sm:text-[38px]">
						Plans designed
						<br />
						for your success
					</h3>
					<p className="mt-4 max-w-lg text-[15px] leading-relaxed text-gray-500">
						Start free with Bronze. Earn reward points as you build your profile, then upgrade for premium features at a
						fraction of the cost.
					</p>
				</div>

				{/* Plan cards */}
				<div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{plans.map((plan) => (
						<PlanCard key={plan.id} plan={plan} usePoints={usePoints} onSelect={setSelectedPackage} />
					))}
				</div>

				<p className="mt-5 text-center text-xs text-gray-400">You may cancel at any time by contacting support.</p>

				{/* Reward Points toggle */}
				<div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-blue-200/80 bg-blue-50/60 px-5 py-4">
					<div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100">
						<Gift className="h-4 w-4 text-blue-700" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-gray-800">
							Save with Reward Points
							{availablePoints !== null && (
								<span className="ml-2 text-blue-700">&middot; {currencyFormat(availablePoints)} pts available</span>
							)}
						</p>
						<p className="text-xs text-gray-500">
							{availablePoints
								? "Toggle to see discounted prices with your earned points"
								: "Earn points by completing your profile, referring friends, and sharing job leads"}
						</p>
					</div>
					<button
						type="button"
						role="switch"
						aria-checked={usePoints}
						aria-label="Toggle reward points"
						onClick={() => setUsePoints(!usePoints)}
						className={cn(
							"relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full p-0.5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
							usePoints ? "bg-blue-600" : "bg-gray-300",
						)}
					>
						<span
							className={cn(
								"pointer-events-none block h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200",
								usePoints ? "translate-x-5" : "translate-x-0",
							)}
						/>
					</button>
				</div>

				{/* ── Compare Plans ──────────────────────────────── */}
				<div className="mt-16 sm:mt-20">
					<h4 className="mb-8 text-center text-2xl font-bold tracking-tight text-gray-900">Compare Plans</h4>

					<div className="-mx-4 overflow-x-auto sm:mx-0">
						<table className="w-full min-w-[680px] text-sm">
							{tableSections.map((section) => (
								<tbody key={section.title}>
									{/* Section header row */}
									<tr>
										<td className="bg-gray-100 py-2.5 pl-5 pr-4 text-[11px] font-bold uppercase tracking-widest text-gray-500">
											{section.title}
										</td>
										{PLAN_NAMES.map((name) => (
											<td
												key={name}
												className={cn(
													"py-2.5 px-3 text-center text-[11px] font-bold uppercase tracking-widest",
													name === "Gold" ? "bg-blue-800 text-white" : "bg-gray-100 text-gray-500",
												)}
											>
												{name}
											</td>
										))}
									</tr>
									{/* Data rows */}
									{section.rows.map((row) => (
										<tr key={row.label} className="border-b border-gray-100">
											<td className="py-3 pl-5 pr-4 text-[13px] text-gray-700">{row.label}</td>
											{row.values.map((val, j) => (
												<td
													key={j}
													className={cn("px-3 py-3 text-center text-[13px] text-gray-700", j === 2 && "bg-blue-50/40")}
												>
													<CellContent value={val} />
												</td>
											))}
										</tr>
									))}
								</tbody>
							))}
						</table>
					</div>
				</div>
			</div>
		</section>
	);
}

export default SubscriptionOptions;
